
import { useEffect, useState, useRef, useCallback, memo } from 'react';
import {
    Box, Flex, HStack, VStack, Text, Heading, Button, 
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { logoutUser } from '../../redux/actions/authActions';
import { clearFrom } from '../../redux/actions/profileActions';
import { OrderSection } from './components/DashboardStyles';
import {
    blackBellIcon,
    blackUser,
    cardIcon,
    lockIcon,
    blackStar,
    helpIcon,
    earningIcon,
} from './components/Icons';
import UplaodCertification from './components/UplaodCertification';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
// const NAVY_DEEP   = '#0F2940';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const DANGER      = '#E53E3E';
const DANGER_BG   = '#FEF2F2';

const FONT      = "'Bricolage Grotesque', sans-serif";
const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const BackArrowIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M19 12H5M12 19l-7-7 7-7"
            stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronRightIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M9 18l6-6-6-6"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const LogoutIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   SETTINGS ITEM — reusable row component
   ───────────────────────────────────────────────────────────── */

const SettingsItem = memo(function SettingsItem({ to, icon, label }) {
    return (
        <Link to={to} style={{ width: '100%', textDecoration: 'none' }}>
            <Flex
                w="full"
                px="14px"
                py="14px"
                align="center"
                justify="space-between"
                bg={SURFACE}
                borderRadius="10px"
                border="1px solid transparent"
                cursor="pointer"
                transition="all 0.15s ease"
                _hover={{
                    bg: SURFACE_ALT,
                    borderColor: BORDER,
                }}
                _active={{
                    bg: SURFACE_ALT,
                    transform: 'translateY(1px)',
                }}
            >
                <Flex align="center" gap="14px" minW="0" flex="1">
                    <Flex
                        align="center"
                        justify="center"
                        w="36px"
                        h="36px"
                        bg={SURFACE_ALT}
                        borderRadius="9px"
                        flexShrink={0}
                    >
                        {icon}
                    </Flex>
                    <Text
                        fontSize="14px"
                        color={INK}
                        fontWeight="600"
                        fontFamily={FONT}
                        noOfLines={1}
                    >
                        {label}
                    </Text>
                </Flex>
                <Box color={FAINT} flexShrink={0}>
                    <ChevronRightIcon />
                </Box>
            </Flex>
        </Link>
    );
});

/* ─────────────────────────────────────────────────────────────
   SETTINGS SECTION — header + items
   ───────────────────────────────────────────────────────────── */

const SettingsSection = memo(function SettingsSection({ title, items }) {
    return (
        <Box w="full">
            <Text
                fontSize="11px"
                color={MUTED}
                fontWeight="800"
                fontFamily={FONT}
                letterSpacing="0.06em"
                textTransform="uppercase"
                mb="8px"
                px="2px"
            >
                {title}
            </Text>
            <VStack
                w="full"
                spacing="2px"
                align="stretch"
                bg={SURFACE}
                border="1px solid"
                borderColor={BORDER}
                borderRadius="12px"
                p="6px"
                boxShadow={SHADOW_SM}
            >
                {items.map((item) => (
                    <SettingsItem
                        key={item.label}
                        to={item.to}
                        icon={item.icon}
                        label={item.label}
                    />
                ))}
            </VStack>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   LOGOUT CONFIRM DIALOG
   ───────────────────────────────────────────────────────────── */

const LogoutConfirmDialog = ({ isOpen, onClose, onConfirm }) => {
    const cancelRef = useRef(null);

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
            isCentered
            motionPreset="slideInBottom"
        >
            <AlertDialogOverlay bg="rgba(15,41,64,0.55)" backdropFilter="blur(4px)">
                <AlertDialogContent
                    bg={SURFACE}
                    borderRadius="14px"
                    fontFamily={FONT}
                    boxShadow="0 20px 60px rgba(15,41,64,0.25)"
                    mx="14px"
                >
                    <AlertDialogHeader
                        fontSize="16px"
                        fontWeight="800"
                        color={INK}
                        fontFamily={FONT}
                        pb="6px"
                    >
                        Log out of your account?
                    </AlertDialogHeader>

                    <AlertDialogBody
                        fontSize="13px"
                        color={MUTED}
                        fontFamily={FONT}
                        lineHeight="1.5"
                        pt="0"
                    >
                        You'll need to sign in again to access your dashboard and orders.
                    </AlertDialogBody>

                    <AlertDialogFooter gap="8px" pt="14px">
                        <Button
                            ref={cancelRef}
                            onClick={onClose}
                            variant="ghost"
                            color={INK}
                            fontWeight="700"
                            fontSize="13px"
                            fontFamily={FONT}
                            h="40px"
                            borderRadius="9px"
                            _hover={{ bg: SURFACE_ALT }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={onConfirm}
                            bg={DANGER}
                            color="white"
                            fontWeight="700"
                            fontSize="13px"
                            fontFamily={FONT}
                            h="40px"
                            borderRadius="9px"
                            _hover={{ bg: '#C53030' }}
                            _active={{ bg: '#B82828' }}
                            leftIcon={<LogoutIcon />}
                        >
                            Log out
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN — SETTINGS PAGE
   ───────────────────────────────────────────────────────────── */

const Settings = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [logoutOpen, setLogoutOpen] = useState(false);

    /* Clear stale 'from' state on mount — defensive against
       UserProfile redirect-on-success race */
    useEffect(() => {
        dispatch(clearFrom());
    }, [dispatch]);

    const handleLogout = useCallback(() => {
        setLogoutOpen(false);
        logoutUser(dispatch);
        navigate('/');
    }, [dispatch, navigate]);

    /* ── Settings items — data driven ────────────────────────── */

    const accountItems = [
        { label: 'Personal profile',           to: '/dashboard/profile/user',         icon: blackUser    },
        { label: 'Payment information',        to: '/dashboard/profile/paymentinfo',  icon: cardIcon     },
        { label: 'Performance & earnings',     to: '/dashboard/profile/performance',  icon: earningIcon  },
        /* TODO: Add proper password route when feature is built */
        { label: 'Password',                   to: '/dashboard/profile/user',         icon: lockIcon     },
        { label: 'Notifications',              to: '#',                               icon: blackBellIcon },
    ];

    const moreItems = [
        { label: 'Rate & review',              to: '#',                               icon: blackStar    },
        { label: 'Help',                       to: '#',                               icon: helpIcon     },
    ];

    return (
        <OrderSection>
            <Box
                maxW="780px"
                mx="auto"
                w="full"
                px={{ base: '14px', md: '20px' }}
                py={{ base: '12px', md: '16px' }}
                pb={{ base: '4rem', lg: '2rem' }}
                fontFamily={FONT}
            >
                {/* HEADER — visible on all breakpoints */}
                <HStack spacing="8px" align="center" mb="20px">
                    <Button
                        onClick={() => navigate(-1)}
                        variant="ghost"
                        size="sm"
                        h="32px"
                        w="32px"
                        minW="32px"
                        p="0"
                        borderRadius="7px"
                        color={MUTED}
                        _hover={{ bg: SURFACE_ALT, color: NAVY }}
                        _focusVisible={{
                            outline: `2px solid ${NAVY}`,
                            outlineOffset: '2px',
                        }}
                        aria-label="Go back"
                    >
                        <BackArrowIcon />
                    </Button>
                    <Heading
                        as="h1"
                        fontSize={{ base: '17px', md: '19px' }}
                        fontWeight="800"
                        color={INK}
                        fontFamily={FONT}
                        letterSpacing="-0.02em"
                    >
                        Settings
                    </Heading>
                </HStack>

                {/* UPLOAD CERTIFICATION — keep at top as before */}
                <Box mb="24px">
                    <UplaodCertification />
                </Box>

                {/* SECTIONS */}
                <VStack w="full" spacing="20px" align="stretch">
                    <SettingsSection title="Account" items={accountItems} />
                    <SettingsSection title="More"    items={moreItems} />
                </VStack>

                {/* LOGOUT */}
                <Box mt="32px">
                    <Button
                        onClick={() => setLogoutOpen(true)}
                        w="full"
                        h="44px"
                        bg={DANGER_BG}
                        color={DANGER}
                        fontWeight="700"
                        fontSize="13px"
                        fontFamily={FONT}
                        borderRadius="10px"
                        border="1px solid"
                        borderColor="transparent"
                        leftIcon={<LogoutIcon />}
                        _hover={{
                            bg: '#FCE7E7',
                            borderColor: DANGER,
                        }}
                        _active={{ bg: '#FAD6D6' }}
                        _focusVisible={{
                            outline: `2px solid ${DANGER}`,
                            outlineOffset: '2px',
                        }}
                    >
                        Log out
                    </Button>
                </Box>
            </Box>

            <LogoutConfirmDialog
                isOpen={logoutOpen}
                onClose={() => setLogoutOpen(false)}
                onConfirm={handleLogout}
            />
        </OrderSection>
    );
};

export default Settings;